<script lang="ts">
export default {
  name: 'DashboardWidget',
};
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue';
import CardTitle from '@/components/ui/card/CardTitle.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import widgetRegistry from './widgetRegistry';
import type { DashboardWidget } from '@/types/v2/DashboardWidget';
import { Trash2, Grip, SlidersHorizontal, Link2, Settings, Unlink2 } from '@lucide/vue';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    widget: DashboardWidget;
    editMode: boolean;
    dragging: boolean;
    maxSpan?: number;
    widthSynced?: boolean;
  }>(),
  {
    maxSpan: 1,
    widthSynced: true,
  },
);

const emit = defineEmits<{
  remove: [id: number];
  configChange: [payload: { id: number; config: Record<string, unknown> }];
  spanChange: [payload: { id: number; colSpan: number }];
  syncWidth: [id: number];
}>();
const attrs = useAttrs();

const definition = computed(() => widgetRegistry[props.widget.widgetType]);
const chromeless = computed(() => definition.value?.chromeless === true);

const parsedConfig = computed(() => {
  if (props.widget.config) {
    try {
      return JSON.parse(props.widget.config);
    } catch {
      return {};
    }
  }
  return definition.value?.defaultConfig ?? {};
});

const localConfig = ref<Record<string, unknown> | null>(null);

watch(
  () => props.editMode,
  (editing) => {
    if (!editing) localConfig.value = null;
  },
);

const displayTitle = computed(() => {
  const def = definition.value;
  if (def?.titleFn) {
    const config = localConfig.value ?? parsedConfig.value;
    return def.titleFn(config);
  }
  return def?.displayName ?? props.widget.widgetType;
});
const effectiveConfig = computed(() => localConfig.value ?? parsedConfig.value);

const colSpanClass = computed(() => {
  const span = clampSpan(props.widget.colSpan);
  const map: Record<number, string> = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
  };
  return map[span] || 'col-span-1';
});

const currentSpan = computed(() => clampSpan(props.widget.colSpan));
const hasSettingsComponent = computed(() => Boolean(definition.value?.settingsComponent));
const showSpanSlider = ref(false);
const spanSliderHostRef = ref<HTMLElement | null>(null);
const spanSliderPanelRef = ref<HTMLElement | null>(null);
const spanSliderStyle = ref<Record<string, string>>({});
const showSettingsPanel = ref(false);

function clampSpan(span: number) {
  return Math.max(1, Math.min(span, props.maxSpan));
}

function updateSpan(span: number) {
  emit('spanChange', { id: props.widget.id, colSpan: clampSpan(span) });
}

function updateSpanSliderPosition() {
  const trigger = spanSliderHostRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const panelWidth = 288; // keep in sync with overlay `w-72`
  const left = Math.min(Math.max(8, rect.left + (rect.width - panelWidth) / 2), Math.max(8, window.innerWidth - panelWidth - 8));
  const top = Math.max(8, rect.top - 6);
  spanSliderStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
  };
}

function toggleSpanSlider() {
  showSettingsPanel.value = false;
  showSpanSlider.value = !showSpanSlider.value;
  if (showSpanSlider.value) {
    updateSpanSliderPosition();
  }
}

function closeSpanSlider() {
  showSpanSlider.value = false;
}

function toggleSettingsPanel() {
  if (!hasSettingsComponent.value) return;
  showSpanSlider.value = false;
  showSettingsPanel.value = !showSettingsPanel.value;
}

function onSpanSliderInput(e: Event) {
  const value = Number((e.target as HTMLInputElement).value);
  updateSpan(value);
}

function handleClickOutside(e: MouseEvent) {
  if (!showSpanSlider.value) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (showSpanSlider.value && (spanSliderHostRef.value?.contains(target) || spanSliderPanelRef.value?.contains(target))) return;
  showSpanSlider.value = false;
}

function handleEscapeKey(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  showSpanSlider.value = false;
  showSettingsPanel.value = false;
}

watch(
  () => props.editMode,
  (editing) => {
    if (!editing) {
      showSpanSlider.value = false;
      showSettingsPanel.value = false;
    }
  },
);

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  document.addEventListener('keydown', handleEscapeKey);
  window.addEventListener('resize', updateSpanSliderPosition);
  window.addEventListener('scroll', updateSpanSliderPosition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  document.removeEventListener('keydown', handleEscapeKey);
  window.removeEventListener('resize', updateSpanSliderPosition);
  window.removeEventListener('scroll', updateSpanSliderPosition, true);
});

function onWidgetConfigChange(config: Record<string, unknown>) {
  localConfig.value = config;
  emit('configChange', { id: props.widget.id, config });
}
</script>

<template>
  <div
    v-bind="attrs"
    :class="[colSpanClass, { 'rounded-xl opacity-50 ring-2 ring-primary': dragging }]"
    class="transition-all duration-200">
    <div
      :class="[
        'rounded-lg',
        chromeless && !editMode
          ? ''
          : chromeless && editMode
            ? 'bg-card text-card-foreground border border-dashed border-base-content/30'
            : 'bg-card text-card-foreground border shadow-md',
      ]">
      <!-- header -->
      <div :class="chromeless && !editMode ? 'p-0' : 'mx-2 p-4 pb-2'">
        <div
          v-if="editMode"
          class="mb-2 flex items-center justify-between">
          <div
            class="drag-handle flex cursor-grab text-base-content/70 hover:text-base-content active:cursor-grabbing"
            @mousedown.stop>
            <Grip :size="16" />
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="hasSettingsComponent"
              class="btn btn-xs"
              :class="showSettingsPanel ? 'text-white btn-primary' : 'btn-ghost'"
              @click.stop="toggleSettingsPanel">
              <span
                v-if="showSettingsPanel"
                class="px-0.5 text-xs font-medium"
                >Done</span
              >
              <Settings
                v-else
                :size="16" />
            </button>
            <div class="relative">
              <button
                ref="spanSliderHostRef"
                class="btn gap-1 px-2 btn-ghost btn-xs"
                @click="toggleSpanSlider">
                <SlidersHorizontal :size="12" />
                <span class="font-semibold">{{ currentSpan }}</span>
              </button>
            </div>
            <Tooltip
              :text="widthSynced ? 'Width is synced to column' : 'Sync width to column'"
              :delay-duration="250"
              position="bottom">
              <button
                class="btn btn-ghost btn-xs"
                :disabled="widthSynced"
                @click="emit('syncWidth', widget.id)">
                <Link2
                  v-if="widthSynced"
                  :size="16" />
                <Unlink2
                  v-else
                  :size="16" />
              </button>
            </Tooltip>
            <button
              class="btn btn-xs"
              @click="emit('remove', widget.id)">
              <Trash2 :size="16" />
            </button>
          </div>
        </div>
        <CardTitle
          v-if="!chromeless || editMode"
          class="text-md font-medium"
          >{{ displayTitle }}</CardTitle
        >
      </div>

      <!-- content -->
      <div :class="chromeless && !editMode ? 'p-0' : 'p-6 pt-0'">
        <component
          v-if="showSettingsPanel && definition?.settingsComponent"
          :is="definition.settingsComponent"
          :config="effectiveConfig"
          @configChange="onWidgetConfigChange" />
        <component
          v-else-if="definition"
          :is="definition.component"
          :config="effectiveConfig"
          :editMode="editMode"
          @configChange="onWidgetConfigChange" />
        <div
          v-else
          class="text-sm text-base-content/50">
          Unknown widget type: {{ widget.widgetType }}
        </div>
      </div>
    </div>
  </div>
  <Teleport to="body">
    <div
      v-if="showSpanSlider"
      ref="spanSliderPanelRef"
      class="z-40 w-72 rounded-xl border bg-base-100 p-4 shadow-lg"
      :style="spanSliderStyle"
      @mouseleave="closeSpanSlider">
      <div class="mb-3 flex items-center justify-between text-sm text-base-content/70">
        <span>Widget width</span>
        <span class="font-semibold text-base-content">{{ currentSpan }}</span>
      </div>
      <input
        class="range w-full range-primary range-sm"
        type="range"
        min="1"
        :max="maxSpan"
        step="1"
        :value="currentSpan"
        @input="onSpanSliderInput" />
      <div class="mt-2 flex justify-between text-xs text-base-content/60">
        <span
          v-for="option in maxSpan"
          :key="option"
          >{{ option }}</span
        >
      </div>
    </div>
  </Teleport>
</template>
